import { useEffect, useRef, type RefObject } from 'react';
import { store } from '@/store';
import type { AppDispatch } from '@/store';
import { appendChatMessage } from '@/store/slices/callSlice';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import { getInitialOrCaptureContactId } from '@/components/amazon-connect/contactUtils';
import { connectParticipantToCallMessage } from '@/utils/chatMessages';
import { playChatMessageTone } from '@/utils/chatMessageTone';
import { notifyChatMessage } from '@/utils/desktopNotification';
import type {
  ConnectChatController,
  ConnectChatMessageEvent,
  ConnectContact,
} from '@/types';
import type { CallMessage } from '@/types/call';

const CHAT_TRANSCRIPT_DEBUG_PREFIX = '[ChatDebug][useChatConnectTranscript]';

function waitUntilChatInitSettled(contactId: string): Promise<void> {
  if (!store.getState().call.sessions[contactId]?.isStreamInitializing) {
    console.log(`${CHAT_TRANSCRIPT_DEBUG_PREFIX} init already settled`);
    return Promise.resolve();
  }
  console.log(`${CHAT_TRANSCRIPT_DEBUG_PREFIX} waiting for chat init`);
  return new Promise(resolve => {
    const unsub = store.subscribe(() => {
      if (!store.getState().call.sessions[contactId]?.isStreamInitializing) {
        unsub();
        console.log(`${CHAT_TRANSCRIPT_DEBUG_PREFIX} chat init settled`, {
          previousMessages: store.getState().call.previousMessages,
        });
        resolve();
      }
    });
  });
}

function appendLiveChatMessage(
  dispatch: AppDispatch,
  contactId: string,
  msg: CallMessage
): void {
  console.log(`${CHAT_TRANSCRIPT_DEBUG_PREFIX} live message received`, { msg });
  playChatMessageTone(msg);
  notifyChatMessage(msg, contactId);
  dispatch(appendChatMessage({ ...msg, contactId }));
}

/**
 * Connect ChatJS for the active chat: live onMessage only.
 * History comes from GET /chat/init; Connect is not used for transcript polling.
 */
export function useChatConnectTranscript(
  isChat: boolean,
  activeContact: ConnectContact | null,
  dispatch: AppDispatch
): RefObject<ConnectChatController | null> {
  const controllerRef = useRef<ConnectChatController | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    console.log(`${CHAT_TRANSCRIPT_DEBUG_PREFIX} effect start`, {
      isChat,
      hasActiveContact: Boolean(activeContact),
    });
    controllerRef.current = null;
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    if (!isChat || !activeContact) {
      console.log(`${CHAT_TRANSCRIPT_DEBUG_PREFIX} effect skipped`, {
        isChat,
        hasActiveContact: Boolean(activeContact),
      });
      return;
    }
    const contactId =
      getInitialOrCaptureContactId(activeContact) ??
      activeContact.getContactId();
    let cancelled = false;
    let initSettled = !store.getState().call.isStreamInitializing;
    const pendingMessages: CallMessage[] = [];

    const handleLiveMessage = (
      dispatch: AppDispatch,
      contactId: string,
      msg: CallMessage
    ) => {
      if (!initSettled) {
        pendingMessages.push(msg);
        console.log(`${CHAT_TRANSCRIPT_DEBUG_PREFIX} live message queued`, {
          msg,
          pendingCount: pendingMessages.length,
        });
        return;
      }
      appendLiveChatMessage(dispatch, contactId, msg);
    };

    connectAdapter
      .getChatMediaController(activeContact)
      .then(async controller => {
        if (cancelled) return;
        controllerRef.current = controller;
        console.log(`${CHAT_TRANSCRIPT_DEBUG_PREFIX} media controller ready`, {
          hasOnMessage: typeof controller.onMessage === 'function',
        });

        const unsub =
          typeof controller.onMessage === 'function'
            ? controller.onMessage((evt: ConnectChatMessageEvent) => {
                console.log(
                  `${CHAT_TRANSCRIPT_DEBUG_PREFIX} raw onMessage`,
                  evt
                );
                const msg = connectParticipantToCallMessage(
                  evt.data ?? {},
                  true
                );
                if (!msg) {
                  console.log(
                    `${CHAT_TRANSCRIPT_DEBUG_PREFIX} onMessage skipped`,
                    { reason: 'not a chat message', evt }
                  );
                  return;
                }
                handleLiveMessage(dispatch, contactId, msg);
              })
            : null;
        unsubRef.current = unsub ?? null;

        await waitUntilChatInitSettled(contactId);
        initSettled = true;
        if (cancelled) return;
        if (pendingMessages.length > 0) {
          console.log(
            `${CHAT_TRANSCRIPT_DEBUG_PREFIX} flushing queued messages`,
            {
              pendingMessages,
            }
          );
          pendingMessages
            .splice(0)
            .forEach(msg => handleLiveMessage(dispatch, contactId, msg));
        }
      })
      .catch(err => {
        console.log(
          `${CHAT_TRANSCRIPT_DEBUG_PREFIX} media controller error`,
          err
        );
        if (!cancelled) controllerRef.current = null;
      });

    return () => {
      console.log(`${CHAT_TRANSCRIPT_DEBUG_PREFIX} cleanup`);
      cancelled = true;
      controllerRef.current = null;
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [isChat, activeContact, dispatch]);

  return controllerRef;
}
